export type Subsystem3D = {
  step: number;
  name: string;
  role: string;
  dims: string;
  material: string;
  fastener: string;
  physics: string;
  color: string;
};

/** Dynamic 3D subsystems for despiece / montaje / blueprints, keyed by project title. */
const ENCLOSURE_SUBSYSTEMS: Subsystem3D[] = [
  {
    step: 1,
    name: "1. Bancada Estructural & Base Antivibración",
    role: "Ancla todo el conjunto mecánico, absorbe vibraciones y define el plano de referencia (Datum A).",
    dims: "180 mm × 140 mm × 16 mm",
    material: "PETG-CF (Fibra de Carbono 20% · 4 Perímetros) / Aluminio 6061",
    fastener: "4× Tornillos Allen M4×16 Inox 316 + Insertos térmicos",
    physics: "Inercia de área (I): mayor espesor en nervaduras multiplica al cubo la rigidez contra flexión.",
    color: "#38bdf8",
  },
  {
    step: 2,
    name: "2. Actuador Primario & Tren de Transmisión Planetario",
    role: "Multiplica el torque del motor reduciendo las RPM mediante engranajes helicoidales/planetarios.",
    dims: "Ø 68 mm × 44 mm · Módulo 1.5 · Relación 12:1",
    material: "Nylon PA12 / PETG-CF + Rodamientos 608RS Sellados",
    fastener: "Eje rectificado Ø 8 mm + Prisionero M3 sobre plano (D-shaft)",
    physics: "Ventaja mecánica (τ_salida = τ_motor × i × η): multiplica la fuerza giratoria conservando la potencia.",
    color: "#10b981",
  },
  {
    step: 3,
    name: "3. Etapa de Potencia DC & Regulación Buck",
    role: "Alimenta los motores con corriente alta sin meter ruido eléctrico al cerebro lógico.",
    dims: "85 mm × 55 mm × 22 mm",
    material: "Fuente / Batería LiFePO4 + Regulador Step-Down LM2596",
    fastener: "Borneras de tornillo 15A + Fusible rápido",
    physics: "Desacoplo de potencia: separa los picos inductivos del motor (12V-24V) de la lógica limpia (3.3V/5V).",
    color: "#f59e0b",
  },
  {
    step: 4,
    name: "4. Cerebro Microcontrolador ESP32-S3 + Sensores I2C",
    role: "Ejecuta el control PID en tiempo real, lee sensores y comunica telemetría WiFi/BLE.",
    dims: "62 mm × 38 mm × 12 mm",
    material: "PCB Doble Capa + Barniz Conformal Anti-Humedad PR",
    fastener: "4× Standoffs M2.5 de latón/nylon",
    physics: "Lazo cerrado de control: mide el error real cada milisegundo y corrige la señal PWM.",
    color: "#a855f7",
  },
  {
    step: 5,
    name: "5. Carcasa Protectora IP67 & Panel Frontal Acotado",
    role: "Protege la electrónica de polvo, salitre e impactos manteniendo acceso a conectores.",
    dims: "184 mm × 144 mm × 88 mm (Tolerancia holgura 0.20 mm)",
    material: "PETG / ASA UV-Estable + Junta de TPU 95A impresa",
    fastener: "6× Tornillos M3×20 pasantes al ras (Countersunk)",
    physics: "Diseño para Manufactura y Ensamble (DFMA): holgura de 0.2 mm garantiza encaje sin lijar.",
    color: "#ec4899",
  },
];

export function getProjectSubsystems3D(title: string): Subsystem3D[] {
  const t = (title || "Proyecto").toLowerCase();
  if (t.includes("mesa") || t.includes("htx")) {
    return [
      {
        step: 1,
        name: "1. Patas Exteriores Base 2.25\" + Suelas TPU 95A (Carpeta 03 y 04)",
        role: "6 columnas base de acero PTR 2.25\"×2.25\"×18\" con suelas antivibración impresas en TPU 95A y bujes guía PETG-HF.",
        dims: "6× PTR 2.25\" × 2.25\" × 18.00\" (Pared 1/8\") + Suela 3.0\"×3.0\"",
        material: "Acero estructural ASTM A500 + TPU 95A + PETG-HF (Bambu Lab)",
        fastener: "Perillas estriadas M10 + Pasador acero Ø 3/8\" + Bridas 4.5\"×4.5\"",
        physics: "Pandeo elástico de Euler y traslape telescópico mínimo > 3× ancho del tubo.",
        color: "#38bdf8",
      },
      {
        step: 2,
        name: "2. Sistema Opcional Engranajes Cónicos 90° + Husillos ACME TR16x4 (Carpeta 05)",
        role: "6 husillos trapezoidales autoblocantes TR16x4 con tuercas bridadas de bronce, cajas de engranajes cónicos a 90° y ejes hexagonales a motores 24V.",
        dims: "6× Husillos Ø 16 mm × 18.5\" (Paso 4 mm) + 6× Cajas 90° 1:1 + 3× Motores 24V",
        material: "Acero C45 rectificado + Bronce Fosforado SAE 660 + Ejes Hex 12 mm",
        fastener: "Bridas de 4 pernos M6 + Acoplamiento hexagonal sin desfase angular",
        physics: "Autobloqueo mecánico por ángulo de hélice (α < φ): la mesa jamás baja sola sin corriente.",
        color: "#a855f7",
      },
      {
        step: 3,
        name: "3. Chasis Superior PTR 2.0\"×1.5\" con Esquina Libre 45° (Carpeta 02)",
        role: "Vigas maestras de 65.25\" (Ala Izq) y 43.75\" (Ala Der) con viga diagonal en voladizo a 45° (cero patas estorbando las rodillas) y bahía libre en Y=34.5\"–60.5\".",
        dims: "72.00\" × 72.00\" × 2.00\" (PTR 2.0\"×1.5\" Calibre 11)",
        material: "Acero PTR Calibre 11 (3.0 mm) + Pintura electrostática negra mate",
        fastener: "24× Pernos hexagonales M8×25 Grado 8.8 en bridas coaxiales",
        physics: "Deflexión máxima δ = F·L³/(48·E·I) < L/600 bajo 150 kg de carga.",
        color: "#10b981",
      },
      {
        step: 4,
        name: "4. Bahía Inteligente CoreXY + Carga Inalámbrica Qi2 (Carpeta 07 — HTX Studio)",
        role: "Rieles lineales X-Y con 2 motores NEMA 17 silenciosos (TMC2209) que mueven la bobina Qi2/MagSafe 15W y anillo LED bajo el cristal del retorno derecho.",
        dims: "Ventana CNC 15.00\" × 22.00\" en coordenadas X=[6.5\", 21.5\"], Y=[37.0\", 59.0\"]",
        material: "Varillas Inox Ø 10 mm + Carritos PETG-HF (Bambu Lab) + Cristal Ahumado 4.5 mm",
        fastener: "Correas GT2 6 mm + Marco biselado PLA-CF empotrado al ras",
        physics: "Acoplamiento inductivo Qi2: entrehierro dieléctrico de 4.5 mm a través de vidrio templado.",
        color: "#f59e0b",
      },
      {
        step: 5,
        name: "5. Cubiertas Madera Sólida: Principal 72\"×28\" + Retorno 44\"×28\" (Carpeta 01)",
        role: "Tablero izquierdo largo de 72\"×28\"×1.5\" y tablero derecho de retorno de 44\"×28\"×1.5\" (total 72\"×72\") unidos a ras con placas inferiores y 3 pasacables Ø 3\".",
        dims: "Cubierta 1 (Izq): 72\"×28\"×1.5\" | Cubierta 2 (Der con Celular): 44\"×28\"×1.5\"",
        material: "Butcher Block Madera Dura 1.5\" (38.1 mm) sellada con poliuretano mate",
        fastener: "Insertos roscados M8 de latón + 3 Placas de unión de acero 1/4\" + 4 Dowels Ø 3/8\"",
        physics: "Unión desmontable Flat-Pack sin tornillos autorroscantes: permite armar y desarmar decenas de veces.",
        color: "#ec4899",
      },
    ];
  }

  // Enclosure / gabinete / printables first — avoid mis-routing to civil templates
  const isEnclosure =
    t.includes("gabinete") ||
    t.includes("carcasa") ||
    t.includes("enclosure") ||
    t.includes("ip67") ||
    t.includes("ip65") ||
    t.includes("bambu") ||
    (t.includes("petg") && !t.includes("casa"));
  const isHouseOrCivil =
    t.includes("casa") ||
    t.includes("concreto") ||
    t.includes("coffee") ||
    t.includes("arquitect");
  const isSolarOrPower =
    t.includes("solar") || t.includes("lifepo4") || t.includes("luma");

  if (isEnclosure) {
    return ENCLOSURE_SUBSYSTEMS;
  }

  if (isHouseOrCivil) {
    return [
      {
        step: 1,
        name: "1. Zapata Corrida & Losa Monolítica 4000 PSI",
        role: "Transmite las cargas gravitacionales al suelo firme y resiste subpresión/vuelco por huracán.",
        dims: "12.0 m × 8.5 m × 0.20 m",
        material: "Hormigón 4000 PSI + Varilla #5 Grado 60 @ 12\"",
        fastener: "Bastones de anclaje en L cada 16 pulgadas",
        physics: "Distribución de esfuerzos (σ = F / A): amplía el área de contacto para que el terreno no ceda.",
        color: "#38bdf8",
      },
      {
        step: 2,
        name: "2. Muros Estructurales Bloque 8\" + Columnas Amarre",
        role: "Muros de corte que resisten empuje lateral de vientos de 175+ mph (ASCE 7 PR).",
        dims: "Altura libre 2.85 m · Espesor 200 mm",
        material: "Bloque de hormigón 8\" celdas llenas con grout",
        fastener: "Escalerilla galvanizada cada 2 hiladas",
        physics: "Resistencia al esfuerzo cortante basal y momento de vuelco aerodinámico.",
        color: "#10b981",
      },
      {
        step: 3,
        name: "3. Canalizaciones Ocultas (MEP: Eléctrico 240V + Pluvial)",
        role: "Red arterial interna para energía solar híbrida, agua de cisterna y datos.",
        dims: "Tubería PVC Schedule 40 Ø 3/4\" y Ø 2\"",
        material: "PVC Sch 40 + Conductores THHN/THWN-2 Cobre",
        fastener: "Cajas octogonales y abrazaderas Inox 316",
        physics: "Hidráulica de tuberías (Bernoulli) y caída de tensión mínima (< 3%).",
        color: "#f59e0b",
      },
      {
        step: 4,
        name: "4. Losa de Techo Inclinada Captadora + Aleros Cortaviento",
        role: "Cierre estructural hermético, aislamiento térmico pasivo y cosecha de lluvia.",
        dims: "Pendiente 2% hacia filtro pluvial · Alero 60 cm",
        material: "Hormigón armado + Sellador elastomérico reflectivo",
        fastener: "Vigas perimetrales sísmicas 12\"×16\"",
        physics: "Efecto Bernoulli en techos: el perfil del alero rompe la succión negativa del huracán.",
        color: "#a855f7",
      },
      {
        step: 5,
        name: "5. Matriz Solar 175 mph + Domótica ESP32 / Sensores",
        role: "Generación autosuficiente, respaldo LiFePO4 y telemetría ambiental inteligente.",
        dims: "Rieles Unistrut + 12 Placas 450W Bifaciales",
        material: "Aluminio Anodizado 6005-T5 + Tornillería Inox 316",
        fastener: "Pernos de expansión química 1/2\" × 5\" en losa",
        physics: "Conversión fotovoltaica y control automático en lazo cerrado.",
        color: "#ec4899",
      },
    ];
  }

  if (isSolarOrPower) {
    return [
      {
        step: 1,
        name: "1. Chasis Estructural & Disipador Base",
        role: "Soporta el peso del banco LiFePO4 (45+ kg) y disipa calor por conducción.",
        dims: "420 mm × 310 mm × 18 mm",
        material: "Aluminio 6061 / Acero Galvanizado Calibre 12",
        fastener: "4× Pernos M8 Inox 316 con arandela de presión",
        physics: "Estática estructural y transferencia térmica por conducción (Ley de Fourier).",
        color: "#38bdf8",
      },
      {
        step: 2,
        name: "2. Celdas Prismáticas LiFePO4 + Barras de Cobre",
        role: "Almacenamiento electroquímico de alta densidad y baja resistencia interna (< 0.3 mΩ).",
        dims: "Configuración 16S (51.2V Nominal) o 4S (12.8V)",
        material: "Litio-Ferrofosfato + Busbars Cobre Estañado",
        fastener: "Tuercas flange M6 a torque exacto 6.0 N·m",
        physics: "Ley de Joule (P = I²·R): un mal apriete eleva la resistencia de contacto y genera calor peligroso.",
        color: "#10b981",
      },
      {
        step: 3,
        name: "3. BMS Inteligente + Shunt INA226 + Fusible Clase T",
        role: "Protección contra sobrecarga, cortocircuito (>150A) y balanceo activo de celdas.",
        dims: "160 mm × 95 mm × 24 mm",
        material: "MOSFETs de baja Rds(on) + Portafusible Policarbonato",
        fastener: "Separadores de nylon M4 + Terminal hidráulico 2/0 AWG",
        physics: "Interrupción de arco DC y medición diferencial por caída en milivoltios (Shunt).",
        color: "#f59e0b",
      },
      {
        step: 4,
        name: "4. Módulo de Telemetría ESP32-S3 + Pantalla OLED",
        role: "Monitoreo en vivo de SoC, temperatura y conmutación automática ante apagón LUMA.",
        dims: "75 mm × 52 mm × 15 mm",
        material: "PCB FR4 + Conformal Coating Silicona (Anti-Salitre)",
        fastener: "4× Tornillos M3×8 Inox",
        physics: "Muestreo ADC y protocolo serie I2C/RS485 aislado.",
        color: "#a855f7",
      },
      {
        step: 5,
        name: "5. Carcasa Exterior IP65 con Ventilación Convectora",
        role: "Blindaje contra humedad tropical del 85% y salitre costero en Puerto Rico.",
        dims: "450 mm × 340 mm × 220 mm",
        material: "PETG-CF / Policarbonato con junta EPDM",
        fastener: "Prensaestopas PG13.5 + Cierres rápidos Inox",
        physics: "Sellado elástico por compresión de O-ring EPDM y convección forzada filtrada.",
        color: "#ec4899",
      },
    ];
  }

  return ENCLOSURE_SUBSYSTEMS;
}
